import { useCallback, useMemo, useRef, useState } from 'react'

type RowSelectionOptions<T> = {
  /** Seleção controlada de fora (ex.: compartilhada entre listas). */
  selected?: Record<string, T>
  onChange?: (next: Record<string, T>) => void
  /** Linha que não entra na seleção nem no "selecionar todos". */
  isDisabled?: (id: string) => boolean
}

/**
 * Checkbox indeterminate no header e Shift+click seleciona o intervalo desde
 * a última linha clicada. `shiftKey` vem de `onClickCapture` porque o
 * `change` do checkbox não carrega modificadores de forma confiável.
 *
 * A seleção guarda a linha inteira: a listagem é paginada no servidor, e o que
 * foi marcado numa página não está mais em `rows` na seguinte.
 */
export const useRowSelection = <T>(
  rows: T[],
  getId: (row: T) => string,
  { selected: controlled, onChange, isDisabled }: RowSelectionOptions<T> = {}
) => {
  const [internal, setInternal] = useState<Record<string, T>>({})
  const anchorRef = useRef<string | null>(null)
  const shiftRef = useRef(false)

  // `getId`/`onChange` chegam inline dos callers; a ref evita recalcular por render
  const getIdRef = useRef(getId)
  getIdRef.current = getId
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange
  const selectedRows = controlled ?? internal

  const update = useCallback((next: Record<string, T>) => {
    if (onChangeRef.current) onChangeRef.current(next)
    else setInternal(next)
  }, [])

  const selectable = useMemo(
    () => rows.filter(row => !isDisabled?.(getIdRef.current(row))),
    [isDisabled, rows]
  )
  const ids = useMemo(() => selectable.map(getIdRef.current), [selectable])
  const selected = useMemo(() => Object.keys(selectedRows), [selectedRows])

  const isSelected = useCallback(
    (id: string) => id in selectedRows,
    [selectedRows]
  )

  const allSelected = ids.length > 0 && ids.every(id => id in selectedRows)
  const someSelected = ids.some(id => id in selectedRows)

  const registerShift = useCallback((event: React.MouseEvent) => {
    shiftRef.current = event.shiftKey
  }, [])

  const toggle = useCallback(
    (row: T) => {
      const id = getIdRef.current(row)

      if (isDisabled?.(id)) return

      const shouldSelect = !(id in selectedRows)
      const anchor = anchorRef.current
      const next = { ...selectedRows }

      if (shiftRef.current && anchor && anchor !== id) {
        const from = ids.indexOf(anchor)
        const to = ids.indexOf(id)

        if (from !== -1 && to !== -1) {
          selectable
            .slice(Math.min(from, to), Math.max(from, to) + 1)
            .forEach(item => {
              if (shouldSelect) next[getIdRef.current(item)] = item
              else delete next[getIdRef.current(item)]
            })
          shiftRef.current = false
          update(next)

          return
        }
      }

      anchorRef.current = id

      if (shouldSelect) next[id] = row
      else delete next[id]

      update(next)
    },
    [ids, isDisabled, selectable, selectedRows, update]
  )

  const toggleAll = useCallback(() => {
    const next = { ...selectedRows }

    selectable.forEach(row => {
      if (allSelected) delete next[getIdRef.current(row)]
      else next[getIdRef.current(row)] = row
    })

    update(next)
  }, [allSelected, selectable, selectedRows, update])

  const clear = useCallback(() => update({}), [update])

  /** Tira ids da seleção sem tocar no resto — some da lista, some da seleção. */
  const deselect = useCallback(
    (ids: string[]) => {
      const removed = ids.filter(id => id in selectedRows)

      if (!removed.length) return

      const next = { ...selectedRows }

      removed.forEach(id => delete next[id])
      update(next)
    },
    [selectedRows, update]
  )

  return {
    selected,
    selectedRows: useMemo(() => Object.values(selectedRows), [selectedRows]),
    isSelected,
    allSelected,
    someSelected,
    toggle,
    toggleAll,
    clear,
    deselect,
    registerShift
  }
}
